import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import logo2 from "../dashboard/dots.png"; // Dots icon
import OrgDetailsDashboard from "./OrgDetailsDashboard";

const OrgDashboard = () => {
  const [data, setData] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [starredAgent, setStarredAgent] = useState(null); // ⭐ Track starred agent for syncing

  useEffect(() => {
    fetchOrgData();
  }, []);

  const fetchOrgData = async () => {
    try {
      const orgID = sessionStorage.getItem("loggedInUser");
      if (!orgID) {
        console.error("No loggedInUser found in sessionStorage");
        return;
      }
  
      const response = await fetch("/validOrganisation/OrgData", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgID }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
  
      const result = await response.json();
  
      // 🔁 Load starred agents from localStorage
      const savedStarredNames = JSON.parse(localStorage.getItem("starredAgents")) || [];
      console.log(savedStarredNames)
      const transformedData = result.map((item, index) => ({
        name: item.agentName,
        data: [
          { name: "Accepted", value: item.Accepted, color: "#FF0080" },
          { name: "Rejected", value: item.Rejected, color: "#1E3A8A" },
        ],
        agentDetails: item,
        starred: savedStarredNames.includes(item.agentName),
        originalIndex: index,
      }));
  
      // ⭐ Sort starred agents first
      transformedData.sort((a, b) => b.starred - a.starred || a.originalIndex - b.originalIndex);
  
      setData(transformedData);
    } catch (error) {
      console.error("Error fetching org data:", error);
    }
  };
  

  const toggleStar = (index) => {
    setData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index].starred = !updatedData[index].starred;
  
      if (updatedData[index].starred) {
        setStarredAgent(updatedData[index]);
      } else {
        setStarredAgent(null);
      }
  
      // ⭐ Save starred agents' names to localStorage
      const starredNames = updatedData.filter(agent => agent.starred).map(agent => agent.name);
      localStorage.setItem("starredAgents", JSON.stringify(starredNames));
  
      // Sort: Starred first, then original order
      updatedData.sort((a, b) => b.starred - a.starred || a.originalIndex - b.originalIndex);
  
      return updatedData;
    });
  };
  

  const handleCardClick = (index) => {
    setSelectedCard(index);
  };

  const closePanel = () => {
    setSelectedCard(null);
  };

  return (
    <div style={styles.mainContainer}>
      <h1 style={styles.heading}>Dashboard</h1>
      <div style={styles.dashboardContainer}>
        {data.map((agent, index) => (
          <div key={index} style={styles.cardWrapper} onClick={() => handleCardClick(index)}>
            <div style={styles.chartContainer}>
              <div style={styles.headerContainer}>
                <h3 style={styles.chartTitle}>{agent.name}</h3>
              </div>
              <PieChart width={250} height={200} style={styles.pieChart}>
                <Pie
                  data={agent.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));
                    return (
                      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                    );
                  }}
                >
                  {agent.data.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
              <div style={styles.legendContainer}>
                {agent.data.map((entry, idx) => (
                  <div key={idx} style={{ ...styles.legendItem, color: "#767676" }}>
                    <span style={{ ...styles.legendDot, backgroundColor: entry.color }}></span>
                    {entry.name} <span style={styles.legendValue}>{entry.value}</span>
                  </div>
                ))}
              </div>
              <div style={styles.tagContainer}>
                <span style={styles.tag}>India</span>
              </div>
              <div style={styles.logoContainer}>
                {/* ⭐ Star Icon (Now Left Aligned) */}
                <svg
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleStar(index);
                  }}
                  style={styles.starIcon}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={agent.starred ? "#FF0080" : "none"}
                  stroke={agent.starred ? "#FF0080" : "grey"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 15 9 22 9 16 14 18 21 12 17 6 21 8 14 2 9 9 9"></polygon>
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedCard !== null && (
        <div style={styles.overlay} onClick={closePanel}>
          <div style={styles.slidePanel} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={closePanel}>X</button>
            <OrgDetailsDashboard
  agentDetails={data[selectedCard].agentDetails}
  closePanel={closePanel}
  starred={data[selectedCard].starred}
  toggleStar={() => toggleStar(selectedCard)}
/>

          </div>
        </div>
      )}
    </div>
  );
};

// 🔹 Updated CSS Styles
const styles = {
  mainContainer: { display: "flex", flexDirection: "column", alignItems: "center", padding: "20px", width: "100%" },
  heading: { fontSize: "24px", fontWeight: "bold", marginBottom: "10px" },
  dashboardContainer: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", justifyContent: "center" },
  cardWrapper: { cursor: "pointer" },
  chartContainer: { display: "flex", flexDirection: "column", alignItems: "center", background: "#fff", padding: "16px", borderRadius: "10px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", width: "250px", position: "relative" },
  headerContainer: { display: "flex", justifyContent: "center", alignItems: "center", width: "100%", marginBottom: "10px" },
  chartTitle: { fontSize: "15px", fontWeight: "normal", flexGrow: 1, color: "#000000DE" },
  pieChart: { position: "relative" },
  legendContainer: { display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: "30px", width: "100%" },
  legendItem: { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", fontSize: "11px", margin: "4px 0" },
  legendDot: { width: "8px", height: "8px", borderRadius: "50%", display: "inline-block", marginRight: "8px" },
  legendValue: { marginLeft: "auto" },
  logoContainer: { display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%", marginTop: "10px" },
  starIcon: { cursor: "pointer" },
  overlay: { position: "fixed", top: 0, right: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.3)", display: "flex", justifyContent: "flex-end" },
  slidePanel: { width: "400px", height: "100%", backgroundColor: "#fff", boxShadow: "-2px 0px 10px rgba(0,0,0,0.2)", padding: "20px", position: "relative" },
  closeButton: { position: "absolute", top: "10px", right: "10px", fontSize: "16px", background: "none", border: "none", cursor: "pointer" },
  tagContainer: {
    display: "flex",
    gap: "5px",
    marginTop: "-18px",
    marginRight: "150px",
  },
  tag: {
    padding: "4px 8px",
    borderRadius: "9px",
    fontSize: "9px",
    border: "1px solid #E0E0E0",
  },
};

export default OrgDashboard;
